/**
 * リビジョン一覧（docs/01-requirements.md §4.5）。
 *
 * これは「何も失わない」保証のための保険であり、日常的に使う画面ではない。
 * **UI は最小でよい**（§4.5）ので凝らない。一覧・全文表示・復元だけ。
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { errorMessage, ErrorNotice } from '@app/components/ErrorNotice';
import { MarkdownView } from '@app/components/MarkdownView';
import { subjectLabel } from '@app/components/subject-label';
import { deleteDraftIfSynced } from '@app/storage/draft-buffer';
import { useToast } from '@app/ui/toast';
import type { Revision, RevisionMeta, RevisionReason } from '@shared/types';
import { resolveBackNavigation } from '@app/views/back-navigation';

/** リビジョンを切った理由の日本語表示（docs/02-architecture.md §6）。 */
const REASON_LABEL: Record<RevisionReason, string> = {
  session_boundary: '執筆セッションの区切り',
  idle: '時間を空けての再開',
  destructive: '破壊的操作の前',
  draft_recovery: '未保存分の退避',
  manual: '手動',
};

function formatDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  // 表示は JST 固定（ADR-011）。
  const d = new Date(ms + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}/${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export function RevisionsPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [list, setList] = useState<RevisionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Revision | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const back = noteId
    ? resolveBackNavigation(location.key, location.state, `/notes/${noteId}/edit`, '/')
    : { to: '/' as const, replace: true };

  const load = useCallback(() => {
    if (!noteId) return;
    setLoading(true);
    api
      .listRevisions(noteId)
      .then((revisions) => {
        setList(revisions);
        setError(null);
      })
      .catch((err: unknown) => setError(err))
      .finally(() => setLoading(false));
  }, [noteId]);

  useEffect(() => load(), [load]);

  const select = useCallback((revisionId: number) => {
    setSelectedLoading(true);
    api
      .getRevision(revisionId)
      .then((revision) => setSelected(revision))
      .catch((err: unknown) => setError(err))
      .finally(() => setSelectedLoading(false));
  }, []);

  const revert = useCallback(
    (revision: Revision) => {
      if (!noteId) return;
      const ok = window.confirm(
        `${formatDateTime(revision.created_at)} の内容へ復元します。\n` +
          '復元しても、いまの内容は履歴に残ります（後から戻せます）。\n' +
          '復元しますか？',
      );
      if (!ok) return;
      void (async () => {
        setBusy(true);
        try {
          await api.revertNote(noteId, revision.id);
          // 復元でヘッドの版が進むので、この端末に残っている**送信済みの**ドラフトは
          // 次に開いたとき「孤児ドラフト」と判定され、身に覚えのない draft_recovery が
          // 履歴へ積まれる（監査 A-7）。送信済みのものだけ片付ける。
          // **未送信の入力が残っている場合は消さない**（絶対規則2。従来どおり退避させる）。
          await deleteDraftIfSynced(noteId).catch(() => undefined);
          toast.show('復元しました');
          if (typeof back.to === 'number') {
            navigate(back.to);
          } else {
            navigate(back.to, { replace: back.replace, state: back.state });
          }
        } catch (err: unknown) {
          toast.show(`復元できませんでした: ${errorMessage(err)}`);
        } finally {
          setBusy(false);
        }
      })();
    },
    [back, navigate, noteId, toast],
  );

  if (!noteId) {
    return <div className="centered-message">ノートが指定されていません。</div>;
  }

  return (
    <div className="page">
      <AppHeader
        title="履歴"
        backTo={back.to}
        backReplace={back.replace}
        backState={back.state}
      />

      <main className="page__body">
        <p className="al-notice al-notice--cream notice-block">
          自動保存のたびには増えません。執筆の区切りや、上書きが起きたときに残ります。
        </p>

        {error != null && <ErrorNotice error={error} onRetry={load} />}
        {loading && <p className="muted centered-note">読み込み中…</p>}
        {!loading && list.length === 0 && error == null && (
          <p className="muted centered-note">まだ履歴はありません。</p>
        )}

        <ul className="revision-list">
          {list.map((revision) => (
            <li key={revision.id} className="revision-row">
              <button
                type="button"
                className="revision-row__button"
                aria-expanded={selected?.id === revision.id}
                onClick={() => (selected?.id === revision.id ? setSelected(null) : select(revision.id))}
              >
                <span className="revision-row__time">{formatDateTime(revision.created_at)}</span>
                <span className="al-chip al-chip--lilac">{REASON_LABEL[revision.reason]}</span>
                <span className="revision-row__excerpt muted">{revision.excerpt}</span>
              </button>

              {selected?.id === revision.id && (
                <div className="revision-detail">
                  <p className="muted">
                    対象日: {subjectLabel(selected.subject_date, selected.subject_date_end)}
                    {selected.title ? ` / タイトル: ${selected.title}` : ''}
                  </p>
                  <MarkdownView source={selected.body} />
                  <button
                    type="button"
                    className="al-button al-button--secondary"
                    disabled={busy}
                    onClick={() => revert(selected)}
                  >
                    この内容へ復元
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {selectedLoading && <p className="muted centered-note">全文を読み込み中…</p>}
      </main>
    </div>
  );
}
